import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" aria-label="蘑菇视频首页">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-play-circle-fill text-2xl text-primary-500"></i>
            </div>
            <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
              蘑菇视频
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-600 bg-primary-100/80'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            {categories.slice(0, 5).map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'text-primary-600 bg-primary-100/80'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
            <div className="relative group">
              <button className="px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap flex items-center gap-1">
                更多 <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-0 mt-1 w-40 bg-background-50 border border-background-200/70 rounded-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {categories.slice(5).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="block px-4 py-2 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {isSearchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索视频..."
                  className="w-48 px-3 py-1.5 text-sm border border-background-300/60 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setIsSearchOpen(false)}
                  className="ml-1 w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-700"
                >
                  <i className="ri-close-line"></i>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setIsSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
            )}
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'text-primary-600 bg-primary-100/80'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索视频..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-background-300/60 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-sm"></i>
                </div>
              </div>
            </form>
            <div className="space-y-0.5">
              <Link to="/" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100">首页</Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100"
                >
                  {cat.name}
                </Link>
              ))}
              <Link to="/about" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100">关于我们</Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}